import React, { Dispatch, SetStateAction, useEffect, useLayoutEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Icon, useTheme } from '@zydalabs/storefront-components';
import useTranslation from 'next-translate/useTranslation';
import { Typography } from '@zydalabs/zac-react';
import { usePersistedCartData, usePersistedPaymentCard, usePersistedWalletUsed } from 'common/hooks';
import { useStorePaymentDetails, useLayout, useUserInfo } from 'service/hooks';
import TapPaymentCard from 'modules/checkoutModule/components/TapPaymentCard';
import TapDisplayCard from 'modules/checkoutModule/components/TapDisplayCard';
import { LANGUAGE_EN, PAYMENT_GATEWAYS, PAYMENT_METHODS, URLS, PAYMENT_METHODS_BY_PRIORITY } from 'common/constants';
import { CustomActionsRegistry, RenderingRegistry } from 'common/lib/registries';
import { IcApplePay, IcBenefit, IcCardOnDelivery, IcCash, IcCreditCard, IcKnet, IcMada, IcMeeza } from 'common/icons';
import { CustomActionsEnum } from 'common/enums';
import { PaymentMethod } from 'service/types';
import { replaceRoute } from 'common/utils';
import Tabs from 'common/components/Layout/components/PaymentTabs';
import { StyledLabel, StyledBanner, StyledContainer, StyledTitleWrapper } from './styles';
import PaymentMethodsLoading from './Loading';
import WalletCard from '../WalletCard';

const PaymentMethodsSection: React.FC<{
  isWalletBalanceCoverOrderAmount: boolean;
  setIsSavingCard: Dispatch<SetStateAction<boolean>>;
  handleTapCardSubmitted: (paymentToken?: string) => void;
  selectedMethod: PaymentMethod;
  setSelectedPaymentMethod(paymentMethod: PaymentMethod): void;
}> = ({
  isWalletBalanceCoverOrderAmount,
  selectedMethod,
  setIsSavingCard,
  handleTapCardSubmitted,
  setSelectedPaymentMethod,
}) => {
  const router = useRouter();
  const theme = useTheme();
  const { t, lang } = useTranslation('payment');
  const { data: userInfo } = useUserInfo();
  const [, setIsWalletUsed] = usePersistedWalletUsed();
  const [persistedPaymentCard, setPersistedPaymentCard, removePersistedPaymentCard] = usePersistedPaymentCard();
  const [cartData] = usePersistedCartData();
  const [formattedPaymentMethods, setFormattedPaymentMethods] = useState<Array<{ name: string; tabValue: string }>>();
  const [isPaymentIFrameVisible, setIsPaymentIFrameVisible] = useState<boolean>(false);
  const { data: paymentData, isLoading, mutate } = useStorePaymentDetails();
  const isManualOrder = RenderingRegistry.getIsManualOrder();
  const walletBalance = userInfo?.walletBalance;
  const isPaymentMethodsTab = formattedPaymentMethods;
  const shouldRenderWalletCard = !!walletBalance && !isManualOrder && !!selectedMethod?.name;

  const { data: layoutSettings } = useLayout();
  useLayoutEffect(() => {
    if (!cartData?.cartId) replaceRoute({ pathname: URLS.HOME }, { locale: router.locale });
    CustomActionsRegistry.setCustomAction({
      key: CustomActionsEnum.UpdatePaymentMethods,
      fn: () => {
        mutate();
        removePersistedPaymentCard();
      },
    });
  }, []);

  const tabComponents = {
    [PAYMENT_METHODS.CREDIT_CARD]: !isManualOrder && (
      <>
        {isPaymentIFrameVisible && (
          <TapPaymentCard
            setPaymentCard={cardData => {
              setPersistedPaymentCard(cardData);
              handleTapCardSubmitted(cardData?.id);
            }}
            setIsSavingCard={setIsSavingCard}
            t={t}
          />
        )}
        {!isPaymentIFrameVisible && persistedPaymentCard && (
          <TapDisplayCard paymentCard={persistedPaymentCard} setIsPaymentIFrameVisible={setIsPaymentIFrameVisible} t={t} />
        )}
      </>
    ),
  };

  const tabIcons = {
    [PAYMENT_METHODS.CREDIT_CARD]: IcCreditCard,
    [PAYMENT_METHODS.KNET]: IcKnet,
    [PAYMENT_METHODS.CASH]: IcCash,
    [PAYMENT_METHODS.CARD_ON_DELIVERY]: IcCardOnDelivery,
    [PAYMENT_METHODS.MEEZA]: IcMeeza,
    [PAYMENT_METHODS.MADA]: IcMada,
    [PAYMENT_METHODS.BENEFIT]: IcBenefit,
    [PAYMENT_METHODS.APPLE_PAY]: IcApplePay,
  };

  const getFormattedPaymentMethods = storePaymentMethods => {
    const tabs = [];

    const filterOfflinePaymentMethods = paymentMethod =>
      ![PAYMENT_METHODS.CARD_ON_DELIVERY, PAYMENT_METHODS.CASH].includes(paymentMethod.name);

    const applePayOnUnSupportedBrowsers = paymentMethod =>
      paymentMethod.name !== PAYMENT_METHODS.APPLE_PAY ||
      (paymentMethod.name === PAYMENT_METHODS.APPLE_PAY && window?.ApplePaySession);

    const filterPaymentMethods = layoutSettings?.isCommissionBased
      ? storePaymentMethods?.filter(filterOfflinePaymentMethods)
      : storePaymentMethods;

    const sortByPriority = (a, b) => PAYMENT_METHODS_BY_PRIORITY.indexOf(a.name) - PAYMENT_METHODS_BY_PRIORITY.indexOf(b.name);

    // [Hotfix] to remove mada till fixed
    filterPaymentMethods
      .filter(method => method.name !== PAYMENT_METHODS.MADA)
      .filter(applePayOnUnSupportedBrowsers)
      .sort(sortByPriority)
      .forEach(paymentMethod => {
        tabs.push({
          name: t(paymentMethod.name.toLowerCase()),
          tabValue: paymentMethod.name,
          component: tabComponents[paymentMethod.name],
          prefix: (
            <Icon
              icon={tabIcons[paymentMethod.name]}
              width="24px"
              mb={1}
              color={selectedMethod?.name !== paymentMethod.name ? `${theme.color.headlineText}80` : ''}
            />
          ),
        });
      });
    return tabs;
  };

  const handleOnSelectPaymentMethod = (paymentMethodId: string) => {
    const { paymentMethods: storePaymentMethods } = paymentData;
    const selectedPaymentMethod = storePaymentMethods.find(
      method => method.id === paymentMethodId || method.name === paymentMethodId,
    );
    setSelectedPaymentMethod(selectedPaymentMethod);

    if (selectedPaymentMethod?.name === PAYMENT_METHODS.CARD_ON_DELIVERY) {
      RenderingRegistry.setIsWalletUsed(false);
      setIsWalletUsed(false);
    }
    setIsPaymentIFrameVisible(
      !isManualOrder &&
        !persistedPaymentCard &&
        selectedPaymentMethod?.name === PAYMENT_METHODS.CREDIT_CARD &&
        paymentData?.paymentGateway?.staticId === PAYMENT_GATEWAYS.TAP,
    );
  };

  useEffect(() => {
    if (paymentData?.paymentMethods?.length > 0)
      setFormattedPaymentMethods(getFormattedPaymentMethods(paymentData?.paymentMethods));
  }, [paymentData, layoutSettings, isPaymentIFrameVisible]);

  const tabsMargin =
    lang === LANGUAGE_EN ? `0 0 ${theme.space[2]}px ${theme.space[4]}px` : `0 ${theme.space[4]}px ${theme.space[2]}px 0`;

  if (!cartData?.cartId || isLoading) return <PaymentMethodsLoading />;

  return (
    <>
      <StyledTitleWrapper>
        <Typography variant="heading16">{t('paymentMethod')}</Typography>
        {layoutSettings?.isCommissionBased && <StyledLabel>{t('cashBackPercent')}</StyledLabel>}
      </StyledTitleWrapper>
      {layoutSettings?.isCommissionBased && (
        <>
          <StyledContainer>
            <StyledBanner>
              <Typography variant="heading16">{t('cashBack')}</Typography>
              <Typography variant="body16" color={theme.color.grey0}>
                {t('rewardCashBack')}
              </Typography>
            </StyledBanner>
          </StyledContainer>
        </>
      )}
      {shouldRenderWalletCard && (
        <WalletCard isOnlinePayment={!!selectedMethod?.id || selectedMethod?.name === PAYMENT_METHODS.CASH} />
      )}
      {isPaymentMethodsTab && (
        <Tabs
          tabs={formattedPaymentMethods}
          value={selectedMethod?.name}
          variant="block"
          showHeader
          onChange={handleOnSelectPaymentMethod}
          margin={tabsMargin}
        />
      )}
    </>
  );
};

export default PaymentMethodsSection;
